/* eslint-disable no-nested-ternary */
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import PropertiesView from '@qpr/components/Properties/views/PropertiesView';
import VerticalScroll from '@qpr/components/ScrollBar/VerticalScroll';
import MapArea from '@qpr/map/components/MapArea';
import PopTooltip from '@qpr/map/components/PopTooltip';
import React from 'react';
import useHierarchy, { Hierarchy } from '../api/useHierarchy';
import useExpandContextWithFilters from '../hooks/useExpandContextWithFilters';
import useHierarchyUrlParams from '../hooks/useHierarchyUrlParams';
import useInitAppBar from '../hooks/useInitAppbar';
import usePropertyViewAttributes from '../hooks/usePropertyViewAttributes';
import FilterContext, { useFilter } from '../modules/Filter/components/FilterContext';
import Search from '../modules/Filter/components/Search';
import useHierarchyFilter from '../modules/Filter/components/useHierarchyFilter';
import useUrlFilters from '../modules/Filter/components/useUrlFilters';
import FilterChipsView from '../modules/Filter/views/FilterChipsView';
import FilterListView from '../modules/Filter/views/FilterListView';
import ExpandContext, { useExpand } from '../modules/Map/components/ExpandContext';
import GroupContainer from '../modules/Map/components/GroupContainer';
import SubItemPopover from '../modules/Map/components/SubItemPopover';

const PREFIX = 'Map2';

const classes = {
  disableBorder: `${PREFIX}-disableBorder`,
  pageHeader: `${PREFIX}-pageHeader`,
  filterChips: `${PREFIX}-filterChips`,
  buttonGroup: `${PREFIX}-buttonGroup`,
  icon: `${PREFIX}-icon`,
  scrollContainer: `${PREFIX}-scrollContainer`,
  scrollContent: `${PREFIX}-scrollContent`,
  errorText: `${PREFIX}-errorText`,
  groupItemCard: `${PREFIX}-groupItemCard`,
  selectedGroupItemCard: `${PREFIX}-selectedGroupItemCard`,
  root: `${PREFIX}-root`,
  subProcess: `${PREFIX}-subProcess`,
  groupItemTitle: `${PREFIX}-groupItemTitle`,
  groupCardSpacer: `${PREFIX}-groupCardSpacer`,
  groupItemDescription: `${PREFIX}-groupItemDescription`,
  groupContainerDescription: `${PREFIX}-groupContainerDescription`,
  groupContainer: `${PREFIX}-groupContainer`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.disableBorder}`]: {
    borderRight: 'none !important'
  },
  [`& .${classes.pageHeader}`]: {
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(4),
    alignItems: 'center',
    color: theme.palette.getContrastText(theme.palette.text.primary),
    flexBasis: '30px',
    overflow: 'visible'
  },
  [`& .${classes.filterChips}`]: {
    alignItems: 'center',
    overflow: 'visible',
    marginLeft: theme.spacing(2)
  },
  [`& .${classes.buttonGroup}`]: {
    float: 'right',
    whiteSpace: 'nowrap',
    color: theme.palette.getContrastText(theme.palette.text.primary)
  },
  [`& .${classes.icon}`]: {
    marginRight: theme.spacing(1)
  },
  [`& .${classes.scrollContainer}`]: {
    borderRadius: '4px'
  },
  [`& .${classes.scrollContent}`]: {
    paddingRight: theme.spacing(1),
    height: '100%',
    position: 'relative',
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    }
  },
  [`& .${classes.errorText}`]: {
    color: theme.palette.getContrastText(theme.palette.text.primary),
    marginTop: '25vh'
  },
  [`& .${classes.groupItemCard}`]: {
    width: '300px',
    height: '230px',
    borderTop: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.default,
    backgroundSize: '100% 200%',
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.default} 50%, ${theme.palette.primary.main} 50%)`,
    textAlign: 'center',
    padding: '8px',
    transition: 'all 0.5s',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  [`& .${classes.selectedGroupItemCard}`]: {
    color: '#fff',
    transition: 'all 0.5s',
    backgroundPosition: '0 -100%'
  },
  [`& .${classes.groupItemTitle}`]: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box',
    lineHeight: '32px',
    minHeight: '64px',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical'
  },
  [`& .${classes.groupItemDescription}`]: {
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical'
  },
  [`& .${classes.subProcess}`]: {},
  [`& .${classes.groupCardSpacer}`]: {},
  [`& .${classes.groupContainerDescription}`]: {
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical'
  },
  [`& .${classes.groupContainer}`]: {}
}));

type MapHierarchy = (item: Hierarchy) => React.ReactNode | null;

const mapItems = (map: MapHierarchy) => (item: Hierarchy) => {
  return item.children?.length > 0 ? item.children.map(map) : null;
};

const View = ({ data }: { data: Hierarchy }) => {
  const [propertyViewItem, setProperyViewItem] = React.useState<Hierarchy | undefined>(undefined);
  const [openSubItem, setOpenSubItem] = React.useState<string | null>(null);

  const handleSubProcessClick = (id: string) => () => {
    if (id === openSubItem) {
      setOpenSubItem(null);
    } else {
      setOpenSubItem(id);
    }
  };

  const setPropertyItem = (item: Hierarchy) => (event: React.MouseEvent<unknown>) => {
    event.stopPropagation();
    setProperyViewItem(item);
  };

  const attributeInfo = usePropertyViewAttributes();

  const { getActiveFilters, getSearchFilter } = useFilter();

  useUrlFilters();
  useExpandContextWithFilters();

  const activeFilters = getActiveFilters();
  const filteredHierarchy = useHierarchyFilter(data, activeFilters);

  const searchFilter = getSearchFilter();
  const hierarchy = useHierarchyFilter(filteredHierarchy, searchFilter ? [searchFilter] : []);

  const { expandAll, collapseAll } = useExpand();

  const getSubItems = mapItems(subItem => (
    <Button
      sx={{
        width: '100%'
      }}
      onClick={setPropertyItem(subItem)}
    >
      <Typography
        style={{
          margin: '8px',
          textAlign: 'center',
          textTransform: 'none',
          textOverflow: 'ellipsis',
          fontWeight: 600,
          width: '100%'
        }}
        variant={'subtitle1'}
        noWrap
      >
        {subItem.name}
      </Typography>
    </Button>
  ));

  const getItems = mapItems(item => (
    <Grid item>
      <Card
        data-testid={'map2-card'}
        className={
          classes.groupItemCard +
          (openSubItem === item.id ? ' ' + classes.selectedGroupItemCard : '')
        }
      >
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          style={{ height: '100%' }}
          onClick={setPropertyItem(item)}
        >
          <Grid item style={{ width: '100%' }}>
            <PopTooltip className={classes.groupItemTitle} text={item.name}>
              <Typography variant={'h5'}>{item.name}</Typography>
            </PopTooltip>
          </Grid>
          <Grid item style={{ textAlign: 'left', width: '100%', flex: 'auto' }}>
            <PopTooltip
              className={classes.groupItemDescription}
              text={item.attributes?.Description?.value || ''}
            >
              {item.attributes?.Description?.value || ''}
            </PopTooltip>
          </Grid>
          <Grid item>
            {item.children?.length > 0 && (
              <SubItemPopover
                title={item.children?.length + ' subprocesses'}
                key={item.id}
                open={openSubItem === item.id}
                onOpen={handleSubProcessClick(item.id)}
                onClose={() => setOpenSubItem(null)}
              >
                {getSubItems(item)}
              </SubItemPopover>
            )}
          </Grid>
        </Grid>
      </Card>
    </Grid>
  ));

  const getGroups = mapItems(group => (
    <Grid item xs={12} className={classes.groupContainer}>
      <GroupContainer
        key={group.id}
        title={group.name}
        onPropertiesClick={() => setProperyViewItem(group)}
        subheader={
          <PopTooltip
            className={classes.groupContainerDescription}
            text={group.attributes?.Description?.value}
          >
            {group.attributes?.Description?.value}
          </PopTooltip>
        }
      >
        {group.children?.length > 0 ? (
          <Grid item>
            <Grid container spacing={4}>
              {getItems(group)}
            </Grid>
          </Grid>
        ) : null}
      </GroupContainer>
    </Grid>
  ));

  return (
    <Root
      sx={{
        paddingRight: 1,
        display: 'flex',
        flexFlow: 'column',
        height: 'calc(100vh - 72px)'
      }}
      className={classes.root}
    >
      <div className={classes.pageHeader}>
        <Typography variant="h4" style={{ display: 'inline-block' }}>
          {hierarchy.name}
        </Typography>
        <ButtonGroup
          variant="text"
          aria-label="view controls"
          className={classes.buttonGroup}
          classes={{ groupedText: classes.disableBorder }}
          color="inherit"
          disabled={propertyViewItem !== undefined}
        >
          <Search />
          <FilterListView elementId={hierarchy.id} disabled={propertyViewItem !== undefined} />
          <Button data-testid="expand-all-button" onClick={() => expandAll()}>
            <ExpandMore className={classes.icon} />
            EXPAND ALL
          </Button>
          <Button data-testid="collapse-all-button" onClick={() => collapseAll()}>
            <ExpandLess className={classes.icon} />
            COLLAPSE ALL
          </Button>
        </ButtonGroup>
      </div>
      <div className={classes.filterChips}>
        <FilterChipsView />
      </div>
      <MapArea>
        <Zoom unmountOnExit in={propertyViewItem !== undefined} timeout={{ enter: 300, exit: 250 }}>
          <div
            style={{
              zIndex: 10,
              position: 'absolute',
              width: '100%',
              height: '100%'
            }}
          >
            <PropertiesView
              customProperties={attributeInfo}
              onClose={() => setProperyViewItem(undefined)}
              itemId={propertyViewItem?.fullid}
              path={propertyViewItem?.parentPath}
              name={propertyViewItem?.name}
            />
          </div>
        </Zoom>
        <VerticalScroll
          containerClassName={classes.scrollContainer}
          contentClassName={classes.scrollContent}
        >
          <Grid container spacing={1}>
            {getGroups(hierarchy) || (
              <Grid item>
                <Typography color={'primary'} sx={{ marginLeft: '16px' }}>
                  {'No contents matching filter criteria found.'}
                </Typography>
              </Grid>
            )}
          </Grid>
        </VerticalScroll>
      </MapArea>
    </Root>
  );
};

const MapPage2 = () => {
  useInitAppBar();

  const hierarchyRequest = useHierarchyUrlParams();

  hierarchyRequest.attributes = { ...hierarchyRequest.attributes, Description: {} };
  const { isLoading, data } = useHierarchy(hierarchyRequest);

  return (
    <FilterContext>
      <ExpandContext>{!isLoading && data ? <View data={data} /> : null}</ExpandContext>
    </FilterContext>
  );
};

export default MapPage2;
